import React, { ReactElement } from 'react'
import { AdType, ComponentType, LayoutComponentProps } from '@sport1/types/web'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import dynamic from 'next/dynamic'
import { outbrainMobileAds } from '../adHelper'
import AdSlot from '@/components/Ads/AdSlot'
import AsyncComponent from '@/components/AsyncComponent'
import NewsFeed from '@/components/NewsFeed'
import ResultTeaser from '@/components/ResultTeaser'
import TopVideoListTeaser from '@/components/TopVideoListTeaser'
import { SELF_LOADING_COMPONENT_TYPES } from '@/helpers/renderBaseChannelComponent'
import { isMobileView } from '@/helpers/pageHelper'
import GenericComponent from '@/components/Generic'

const Outbrain = dynamic(() => import('@/components/Ads/AdSlot/OutbrainAd'))

const renderSideColumnComponent = ({
    component,
    isLast,
    isRenderedByAsyncComponent = false,
}: {
    component: LayoutComponentProps
    isLast?: boolean
    isRenderedByAsyncComponent?: boolean
}): ReactElement | null => {
    let sideColumnItem
    const { type } = component
    const isNewsFeedTeaserCard = component.type === ComponentType.NEWS_FEED_TEASER_CARD
    const key = `render-side-column-${component.type}-${component?.componentKey}-${component?.uuid}`
    let hideElement = false

    if (
        component.lazy &&
        SELF_LOADING_COMPONENT_TYPES.includes(component.type) &&
        component.url &&
        !isRenderedByAsyncComponent
    ) {
        return <AsyncComponent key={key} type={component.type} url={component.url} isRightColumn />
    }

    if (
        type === ComponentType.NEWS_FEED_LIST_TEASER ||
        type === ComponentType.NEWS_FEED_TEASER_CARD
    ) {
        sideColumnItem = (
            <NewsFeed
                key={key}
                content={component.content}
                actionButtons={component.actionButtons}
                tags={component.tags}
                hasHeadline={!isNewsFeedTeaserCard}
            />
        )
    } else if (type === ComponentType.TOP_TEASER_LIST) {
        sideColumnItem = <TopVideoListTeaser key={key} teaser={component.content} />
    } else if (type === ComponentType.AD && component.ad?.adType === AdType.DISPLAY) {
        sideColumnItem = <AdSlot key={key} {...component} disableBottomSpace hasBorder />
    } else if (
        type === ComponentType.AD &&
        component.ad?.adType === AdType.OUTBRAIN_AD &&
        component.ad?.widgetIdentifiers?.web
    ) {
        const dataWidgetId = component.ad.widgetIdentifiers.web
        hideElement = !isMobileView() && outbrainMobileAds.includes(dataWidgetId.toUpperCase())
        sideColumnItem = <Outbrain dataWidgetId={dataWidgetId} key={key} />
    } else if (type === ComponentType.RESULT_TEASER) {
        if (component.content?.length) {
            sideColumnItem = (
                <ResultTeaser
                    isRightColumn
                    forceMobileView
                    componentData={component}
                    disableContentSpaceX
                    trackingInteractionLabel="result-teaser"
                />
            )
        }
    } else if (type === ComponentType.GENERIC) {
        sideColumnItem = <GenericComponent {...component} isRightColumn />
    }
    return hideElement ? null : (
        <NonFlexingContainer
            key={key}
            marginBottom={!isLast && component.type !== 'GENERIC' ? 'spacing-6' : 'spacing-none'}
            testID={key}
        >
            {sideColumnItem}
        </NonFlexingContainer>
    )
}

export default renderSideColumnComponent
